import SuperError from "super-error";
import URI from "urijs";

// Fix typing issue to ensure that axios conf accepts `axios-retry` attributes
import "axios-retry";
import type { GrandCentral, PollService, Primitives } from "@busbud/core-types";
import type { GeoEntity } from "@busbud/core-types/dist/lib/geo-service/api";
import type { FareType } from "@busbud/core-types/dist/lib/grand-central/db";
import type { PublicTrip } from "@busbud/core-types/dist/lib/poll-service/api";
import type {
  CacheStateResponse,
  GetTripRelatedResponse
} from "@busbud/core-types/dist/lib/poll-service/api/response";
import type { SearchPassenger } from "@busbud/core-types/dist/lib/search-info";
import type { VehicleType } from "@busbud/int-schemas/lib/vehicles";

import type { DebuggingContext } from "@app/configurable-components/debugging-context/debugging-context";
import type { PassengerGender } from "@app/constants/passengers";
import { toMaybeApiError } from "@app/helpers/api-error";
import { isGeoid } from "@app/helpers/geoid-validation";
import { retryCondition } from "@app/lib/http-clients/helper";
import type {
  HTTPClient,
  HTTPClientConfig,
  HTTPClientResponse
} from "@app/lib/http-clients/http-client-types";
import URIExpand from "@app/lib/uri-expand";
import type { UserPreferences } from "@app/lib/user-preferences";
import { getWindowPollServiceSearchParams } from "@app/modules/async-cart/helpers/get-window-poll-service-search-params";
import type { CartPollResponse } from "@app/modules/checkout/types/cart-v2";
import type { ClientIpHeaders } from "@app/services/security/client-ip-headers";
import type { InMemoryHttpCache } from "@app/services/utils/in-memory-http-cache";
import type { CartAdapter } from "@app/types/checkout-cart-adapter-types";
import type { CheckoutReduxState } from "@app/types/checkout-redux-types";
import type {
  BillingInfo,
  CartContext,
  CartPurchase
} from "@app/types/checkout-types";
import type { CurrencyCode } from "@app/types/currency";
import type { ExperimentsVariations } from "@app/types/experiments";
import type { CreditsActivity } from "@app/types/loyalty";
import type { NapiPurchase } from "@app/types/napi-charge-types";
import type { NapiDepartureDetailsResponse } from "@app/types/napi-departures-types";
import type {
  CityMappingResponse,
  CityMappingThirdParties,
  NapiCity,
  NapiLocation,
  SearchSuggestionsV2,
  Station
} from "@app/types/napi-geo-entity-types";
import type {
  NapiFetchDeparturesResponse,
  PollDeparturesReturnResponse
} from "@app/types/napi-poll-types";
import type { NapiPurchaseResponse } from "@app/types/napi-purchase-types";
import type { NapiRoute } from "@app/types/napi-route-types";
import type {
  AuthResultVerified,
  CreditCard,
  FeaturedCountry,
  InstallmentsPlan,
  InstallmentsPlansParams,
  LoginResponse,
  NapiBookingStatus,
  NapiOperator,
  NapiPartnerProfile,
  NapiPartnerProfileResponse,
  NapiPassenger,
  NapiPopularPlace,
  NapiPurchaseChargesResponse,
  NapiPurchaseStatusResponse,
  NapiRawState,
  NapiSource,
  NapiUser,
  OperatorDetails,
  PartnerPurchaseCartParams,
  PointOfInterest,
  PurchaseCartParams,
  RouteGeohashes,
  RouteRequestOptions,
  SignupParams,
  SignupResponse,
  SourceAccessListResponse,
  SourceStatus,
  StatsCity,
  UpdateCartParams,
  UserBillingAddress,
  ValidateDiscountCodeResponse
} from "@app/types/napi-types";
import type {
  NapiAmount,
  NapiRedeemableAmounts,
  NapiV2Address,
  NapiV2PurchaseCartParams,
  Purchaser
} from "@app/types/napi-v2";
import type { Ancillary } from "@app/types/napi-v2-ancillary";
import type {
  NapiCancellationOption,
  NapiPurchaseCancellation,
  NapiPurchaseCancellationOptions
} from "@app/types/napi-v2-cancellation-types";
import type {
  NapiV2Cart,
  NapiV2CartTrip,
  NapiV2CreateCartRequestOptions,
  NapiV2CreateCartResponse,
  UpdateCartTripParams,
  UpdateCartTripPayload
} from "@app/types/napi-v2-cart-types";
import type { NapiV2CartCharges } from "@app/types/napi-v2-charges-types";
import type {
  NapiV2InstallmentsPlan,
  NapiV2Purchase,
  NapiV2PurchaseDetails
} from "@app/types/napi-v2-purchase-types";
import type {
  CreditType,
  ListCancellationPathParams,
  ListCancellationQueryString,
  PurchaseCancellationBody,
  PurchaseCancellationOption,
  PurchaseCancellationPathParams,
  PurchaseCancellationQueryString
} from "@app/types/napi-v2-request-type";
import type { SearchInfo } from "@app/types/search-types";
import type { PassengerSeatSelectionInstance } from "@app/types/seat-selection-types";

import type { SupportedLanguage } from "../../config/locales";
import type {
  DynamicWhitelabel,
  MaybeWhitelabel,
  PartnerConfig
} from "@partners/partner.type";
import type { Store } from "@reduxjs/toolkit";

import sentry from "./sentry-facade";
import sentryFacade from "./sentry-facade";
import {
  ACCEPT_HEADER_V3,
  ACCEPT_HEADER_V4,
  createServerHeaders
} from "./utils/headers";
import { Locales } from "../../config/locales";
import { MAX_NUM_REVIEWS } from "../constants/rating";
import { getDiscountsQueryAttributes } from "../helpers/search";
import * as UdmHelpers from "../helpers/udm";

const APPLE_PAY_VALIDATE_MERCHANT_URL = "/apple-pay/validate-merchant";
const CART_PURCHASE_TIMEOUT = 10000;
const CART_RETRY_CONFIGURATION = {
  retries: 10
};
const INVENTORY_RETRY_CONFIGURATION = {
  retries: 8
};

export const GET_ROUTE_SOURCES_TIMEOUT = 3 * 1000;

const RETRY_STATUS_CODE = [429, 502, 503];

const OutdatedPollingUrlError = SuperError.subclass(
  "OutdatedPollingUrl",
  function (poll_restart_url) {
    this.message =
      "The polling url refers to a search that is no longer available in Poll Service cache. Please use the poll_restart_url to trigger a new search an return a valid poll_url.";
    this.poll_restart_url = poll_restart_url;
  }
);

const buildQueryStringsObject = (options: Record<string, unknown> = {}) =>
  Object.keys(options).reduce<Record<string, unknown>>(
    (qs, option_key) => ({
      ...qs,
      [option_key]: options[option_key]
    }),
    {}
  );

export type NapiClientConstructorOptions = Partial<{
  request_url: string;
  lang: string;
  locale: string;
  currency: CurrencyCode;
  token: string;
  domain: string;
  request_id: string;
  whitelabel_key: MaybeWhitelabel;
  legacy_partner_settings: PartnerConfig["whitelabel_settings"] | null;
  log_level: string;
  backend_region: string;
  user_agent_quality: string;
  feature_flags: string | null;
  experiments: ExperimentsVariations;
  geo_headers: { [key: string]: string };
  apm_client?: any;
  httpClient: HTTPClient;
  client_ip_headers: ClientIpHeaders;
  inMemoryHttpCache: InMemoryHttpCache | null;
  debugging_context: DebuggingContext;
}>;

type GetLocationStationResponse = {
  station: Station;
  city: NapiCity;
};

export interface GetProfilesResponse {
  profiles: NapiPartnerProfileResponse[];
  count: number;
}

/** @returns {NapiClient} (Note that this doesn't actually help type-check, but it will link the implementation to the definition if one is to search the project.) */

/**
 * Note:
 * We have a lot of methods mixed between old and new API.
 * You can easily find the methods from new API by looking for the `ACCEPT_HEADER_V3` header.
 */
export class NapiClient {
  private options: NapiClientConstructorOptions;
  private readonly inMemoryHttpCache: InMemoryHttpCache | null;

  public constructor(options: NapiClientConstructorOptions) {
    const lang = options.lang || "en";
    const locale = options.locale || "";
    const currency = options.currency || "USD";
    const domain = options.domain || "/napi";
    const request_id = options.request_id;
    const user_agent_quality = options.user_agent_quality;
    const experiments = options.experiments;

    let token = options.token;
    const whitelabel_token =
      options.legacy_partner_settings &&
      options.legacy_partner_settings.napi_auth_token;
    if (whitelabel_token) {
      token = whitelabel_token;
    }

    this.options = {
      lang: lang,
      locale: locale,
      currency: currency,
      token: token,
      domain: domain,
      request_id: request_id,
      log_level: options.log_level,
      user_agent_quality: user_agent_quality,
      experiments: experiments,
      feature_flags: options.feature_flags,
      backend_region: options.backend_region,
      geo_headers: options.geo_headers,
      apm_client: options.apm_client,
      httpClient: options.httpClient,
      client_ip_headers: options.client_ip_headers,
      request_url: options.request_url,
      debugging_context: options.debugging_context,
      whitelabel_key: options.whitelabel_key
    };
    this.inMemoryHttpCache = options.inMemoryHttpCache ?? null;
  }

  public request(config: HTTPClientConfig): HTTPClientResponse {
    // Setup headers
    config.headers = this._setupHeaders(config);

    // Setup querystring
    var qs = config.qs || {};
    var qs_default_params =
      config.default_params === undefined ? true : config.default_params;
    if (qs_default_params) {
      qs.lang = qs.lang ? qs.lang : this.options.lang;
      qs.locale = qs.locale ? qs.locale : this.options.locale;
      qs.currency = qs.currency ? qs.currency : this.options.currency;
    }
    // And language and currency querystring
    const base_uri = new URI(config.url);
    config.url = base_uri.addQuery(qs).toString();
    config.proxy = false;

    if (config.http_caching_enabled && this.inMemoryHttpCache) {
      const cached = this.inMemoryHttpCache.get(config);
      if (cached) {
        return Promise.resolve(cached);
      }
    }

    // @ts-expect-error
    return this.options.httpClient(config).then(
      res => {
        if (config.http_caching_enabled && this.inMemoryHttpCache) {
          this.inMemoryHttpCache.put(config, res);
        }
        this._reportBreadcrumb(config, res.status_code);

        this.options.debugging_context?.addAxiosRequestToDebuggingInfo(
          config,
          res.status_code,
          res.headers["x-cache"]
        );

        return res;
      },
      err => {
        const status_code: number = err.response ? err.response.status : 0;
        const reason: string | undefined = err.message
          ? err.message
          : undefined;
        this._reportBreadcrumb(config, status_code, reason);

        this.options.debugging_context?.addAxiosRequestToDebuggingInfo(
          config,
          status_code,
          err.response?.headers?.["x-cache"]
        );

        throw err;
      }
    );
  }

  // Setup headers to be sent on every request to napi
  private _setupHeaders(config: HTTPClientConfig) {
    const { request_id_override, request_id_separator } = config.options ?? {};
    const request_id =
      request_id_override !== undefined
        ? request_id_override || undefined
        : this.options.request_id;

    return createServerHeaders({
      ...this.options,
      base_headers: config.headers ?? {},
      request_id,
      request_id_separator
    });
  }

  // Add http breadcrumb on Sentry
  private _reportBreadcrumb(
    config: HTTPClientConfig,
    status_code: number,
    reason?: string
  ) {
    sentry.addBreadcrumb({
      type: "http",
      category: "xhr",
      data: {
        url: config.url,
        method: config.method,
        status_code,
        ...(reason ? { reason } : {})
      }
    });
  }

  public setToken(token: string | null) {
    this.options.token = token || undefined;
  }

  public setCurrency(currency: CurrencyCode) {
    this.options.currency = currency;
  }

  public setRequestId(requestId: string) {
    this.options.request_id = requestId;
  }

  public async getSearchCacheMetadata(
    origin_geohash: Primitives.Geohash6,
    destination_geohash: Primitives.Geohash6,
    dates: PollService.Metadata.SearchCacheMetadataDates[]
  ): Promise<PollService.API.SearchCacheMetadataResponse> {
    const routes = [
      {
        origin_geohash,
        destination_geohash
      }
    ];

    const data: PollService.API.SearchCacheMetadataRequest = {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      currency: this.options.currency!,
      routes,
      dates,
      curate_results: true
    };

    return this.request({
      url: this.options.domain + "/metadata/searches",
      method: "POST",
      data,
      default_params: false,
      headers: {
        Accept: ACCEPT_HEADER_V3
      },
      timeout: 4000
    })
      .then(res => res.body)
      .catch(err => {
        sentry.captureException(err, {
          tags: {
            request: "get_search_cache_metadata"
          }
        });

        throw err;
      });
  }

  public pollTrips(
    poll_url: string,
    options?: {
      forward_request_id?: string;
      cached?: boolean;
    }
  ): Promise<PollService.API.PollRelatedResponse> {
    let updated_poll_url = `${poll_url}&format=related`;
    if (options?.cached) {
      updated_poll_url = updated_poll_url.replace("/poll?", "/cached?");
    }

    const request_id_override = options?.forward_request_id || null;

    return this.request({
      url: this.options.domain + updated_poll_url,
      method: "get",
      default_params: false,
      headers: {
        Accept:
          this.options.experiments?.udmv4 === "enabled"
            ? ACCEPT_HEADER_V4
            : ACCEPT_HEADER_V3
      },
      options: {
        request_id_override,
        // Ref: https://github.com/busbud/public-website/pull/13024#discussion_r729167478
        request_id_separator: "--"
      },
      "axios-retry": INVENTORY_RETRY_CONFIGURATION,
      timeout: 30000
    })
      .then(res => res.body)
      .catch(err => {
        sentry.captureException(err, {
          tags: {
            request: "poll_trips"
          }
        });

        throw err;
      });
  }

  public getTrip(
    trip_id: string,
    format?: "related" | "inline",
    details?: boolean
  ): Promise<{ trip: PublicTrip }> {
    return this.request({
      url: `${this.options.domain}/trips/${trip_id}`,
      method: "get",
      qs: {
        lang: this.options.lang,
        locale: this.options.locale,
        details,
        format
      },
      default_params: false,
      headers: {
        Accept: ACCEPT_HEADER_V3
      },
      timeout: 30000
    })
      .then(res => res.body)
      .catch(err => {
        sentry.captureException(err, {
          tags: {
            request: "get_trip"
          }
        });

        throw err;
      });
  }

  public getTripDetailsRelated(
    trip_id: string
  ): Promise<GetTripRelatedResponse> {
    return this.getTrip(
      trip_id,
      "related",
      true
    ) as unknown as Promise<GetTripRelatedResponse>;
  }

  public getTripSegmentStopsRelated(
    trip_id: string
  ): Promise<PollService.API.GetAllSegmentStopsRelatedResponse> {
    return this.request({
      url: `${this.options.domain}/trips/${trip_id}/stops`,
      method: "get",
      qs: {
        format: "related",
        lang: this.options.lang,
        locale: this.options.locale
      },
      default_params: false,
      headers: {
        Accept: ACCEPT_HEADER_V3
      },
      timeout: 30000
    })
      .then(res => res.body)
      .catch(err => {
        sentry.captureException(err, {
          tags: {
            request: "get_trip_stops"
          }
        });

        throw err;
      });
  }

  public async getTripSegmentVehicle(
    trip_id: string,
    segment_id: string
  ): Promise<PollService.API.GetVehicleResponse> {
    return this.request({
      url: `${this.options.domain}/trips/${trip_id}/segments/${segment_id}/vehicle`,
      method: "get",
      qs: {
        lang: this.options.lang,
        locale: this.options.locale,
        currency: this.options.currency
      },
      default_params: false,
      headers: {
        Accept: ACCEPT_HEADER_V3
      },
      timeout: 30000
    })
      .then(res => res.body)
      .catch(err => {
        sentry.captureException(err, {
          tags: {
            request: "get_trip_vehicle"
          }
        });

        throw err;
      });
  }
  /**
   * Initialize a search for trips that are not interconnected and returns polling urls
   *
   * @param search
   * @param route
   * @param poll_query_params
   */
  public initTripsSearch(
    search: SearchInfo,
    route: NapiRoute,
    poll_query_params: Partial<PollService.API.SearchQueryParams>
  ): Promise<PollService.API.SearchResponse> {
    const enable_geo_entity_search =
      isGeoid(search.origin) || isGeoid(search.destination);

    if (
      enable_geo_entity_search &&
      route.origin_geo_entity &&
      route.destination_geo_entity
    ) {
      return this.initGeoEntitiesTripsSearch(
        search,
        { currency: this.options.currency },
        {
          origin_geo_entity: route.origin_geo_entity,
          destination_geo_entity: route.destination_geo_entity
        },
        poll_query_params
      ).catch(err => {
        sentry.captureException(err, {
          tags: {
            request: "init_trip_search"
          }
        });

        throw err;
      });
    } else {
      return this.initAnyTripsSearch(
        search,
        route,
        {},
        poll_query_params
      ).catch(err => {
        sentry.captureException(err, {
          tags: {
            request: "init_trip_search"
          }
        });

        throw err;
      });
    }
  }

  public initGeoEntitiesTripsSearch(
    search: Omit<SearchInfo, "origin" | "destination">,
    options: {
      currency: CurrencyCode | undefined;
    },
    route: {
      origin_geo_entity: Pick<SearchSuggestionsV2, "id" | "place_type">;
      destination_geo_entity: Pick<SearchSuggestionsV2, "id" | "place_type">;
    },
    poll_query_params: Partial<PollService.API.SearchQueryParams>,
    headers: Record<string, string> = {}
  ) {
    const data: PollService.API.SearchRequest = {
      legs: UdmHelpers.getLegsGeoEntity(search, route),
      passengers: UdmHelpers.getPassengers(search),
      options: {
        lang: this.options.lang,
        locale: this.options.locale,
        currency: options.currency,
        include_sold_out: true,
        curate: true
      }
    };

    return this.request({
      url: this.options.domain + "/searches",
      method: "post",
      data,
      params: poll_query_params,
      headers: {
        Accept:
          this.options.experiments?.udmv4 === "enabled"
            ? ACCEPT_HEADER_V4
            : ACCEPT_HEADER_V3,
        ...headers
      }
    }).then(res => res.body);
  }

  /**
   * Initialize a search for interconnected trips and returns polling urls
   *
   * @param search
   * @param route
   * @param poll_query_params
   */
  public initInterconnectedTripsSearch(
    search: SearchInfo,
    route: NapiRoute,
    poll_query_params: Partial<PollService.API.SearchQueryParams>
  ): Promise<PollService.API.SearchResponse> {
    return this.initAnyTripsSearch(
      search,
      route,
      { interconnect: true },
      poll_query_params
    ).catch(err => {
      sentry.captureException(err, {
        tags: {
          request: "init_interconnected_trip_search"
        }
      });

      throw err;
    });
  }

  /**
   * Initialize a trip search and returns polling urls
   *
   * @param search
   * @param route
   * @param options
   * @param poll_query_params
   */
  private initAnyTripsSearch(
    search: SearchInfo,
    route: NapiRoute,
    options: Partial<PollService.API.SearchRequest["options"]> | undefined = {},
    poll_query_params: Partial<PollService.API.SearchQueryParams>
  ): Promise<PollService.API.SearchResponse> {
    const data: PollService.API.SearchRequest = {
      legs: UdmHelpers.getLegs(search, route),
      passengers: UdmHelpers.getPassengers(search),
      options: {
        ...options,
        lang: this.options.lang,
        locale: this.options.locale,
        currency: this.options.currency,
        include_sold_out: true,
        curate: true
      }
    };

    return this.request({
      url: this.options.domain + "/searches",
      method: "post",
      data,
      params: poll_query_params,
      headers: {
        Accept: ACCEPT_HEADER_V3
      }
    }).then(res => res.body);
  }

  public createCart(
    search: SearchInfo,
    outbound_id: string,
    passengers: PassengerSeatSelectionInstance[] | false
  ) {
    /**
     * Need access to experiment/feature flags
     */
    const store = window.store;
    const config = store?.getState()?.config;

    var url = this.options.domain + "/cart";

    var data: { [key: string]: any } = {
      origin_city: search.origin,
      destination_city: search.destination,
      outbound_date: search.outbound_date,
      return_date: search.return_date,
      adult: search.adult,
      child: search.child,
      senior: search.senior,
      adult_wheelchair: search.adult_wheelchair,
      child_wheelchair: search.child_wheelchair,
      senior_wheelchair: search.senior_wheelchair,
      child_ages: search.child_ages,
      senior_ages: search.senior_ages,
      child_wheelchair_ages:
        search.child_wheelchair_ages && search.child_wheelchair_ages.join(","),
      senior_wheelchair_ages:
        search.senior_wheelchair_ages &&
        search.senior_wheelchair_ages.join(","),
      ...getDiscountsQueryAttributes(search),
      discount_code: search.discount_code,
      departure_id: outbound_id,
      supported_features: [
        "adyen_additional_action_payment_methods",
        "priced_seats"
      ],
      payment_platforms: config?.experiments?.STITCH_ENABLED
        ? ["adyen", "spreedly", "stitch"]
        : ["adyen", "spreedly"],
      passengers
    };

    /** Price Modifying questions are currently only supported on one way trips **/
    if (!search.return_date) {
      data.supported_features.push("price_modifying_passenger_questions");
    }

    return this.request({
      url: url,
      method: "post",
      data: data,
      "axios-retry": CART_RETRY_CONFIGURATION
    }).then(response => {
      return {
        cart: response.body.data,
        related: response.body.related
      };
    });
  }

  public createCartV2(
    options?: Partial<NapiV2CreateCartRequestOptions>
  ): Promise<NapiV2CreateCartResponse> {
    const url = this.options.domain + "/carts";
    /**
     * Need access to experiment/feature flags
     */
    const store = window.store;
    const config = (store as Store<CheckoutReduxState>)?.getState()?.config;

    const utm_params = getWindowPollServiceSearchParams();

    const data: NapiV2CreateCartRequestOptions = {
      ...utm_params,
      ...(options || {}),
      supported_payment_providers: [
        "adyen",
        "spreedly",
        "metro-turizm",
        "stitch",
        "generic-redirect"
      ],
      supported_features: [
        "adyen_additional_action_payment_methods",
        "priced_seats",
        "no_payment",
        "price_modifying_passenger_questions"
      ]
    };

    if (config?.experiments?.ENABLE_OXXO_PAYMENT) {
      data.supported_features.push("adyen_voucher_payment_methods");
    }

    if (config?.experiments?.YUNO_PAYMENT) {
      data.supported_payment_providers.push("yuno");
    }

    return this.request({
      url: url,
      method: "post",
      data: data,
      "axios-retry": CART_RETRY_CONFIGURATION,
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      }
    }).then(response => response.body);
  }

  public addTripToCartV2(
    cart_id: NapiV2CreateCartResponse["id"],
    trip_id: string,
    passengers: SearchPassenger[]
  ): Promise<NapiV2CartTrip> {
    const url = `${this.options.domain}/carts/${cart_id}/trips`;

    const data = {
      trip_id,
      passengers
    };

    return this.request({
      url: url,
      method: "post",
      data: data,
      "axios-retry": {
        retries: CART_RETRY_CONFIGURATION.retries,
        retryCondition: err => {
          const status_code = err.response ? err.response.status : 0;
          const should_retry =
            err.isAxiosError &&
            (retryCondition(err) || RETRY_STATUS_CODE.includes(status_code));

          sentry.addBreadcrumb({
            message: err.message,
            category: "axios retry",
            level: "warning",
            data: err
          });

          return should_retry;
        }
      },
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      }
    }).then(response => response.body);
  }

  public getTicket(ticket_id: string, purchase_uuid: string) {
    var url = this.options.domain + "/tickets/" + ticket_id;

    return this.request({
      url: url,
      method: "get",
      qs: {
        purchase_uuid: purchase_uuid
      }
    });
  }

  public getUser(user_id: number): Promise<NapiUser> {
    var url = this.options.domain + "/users/" + user_id;

    return this.request({
      url: url,
      method: "get",
      headers: {
        Accept: ACCEPT_HEADER_V3
      }
    }).then(response => {
      return response.body;
    });
  }

  public getPassengers(options?: {
    include_passenger_question_answers?: boolean;
  }): Promise<NapiPassenger[]> {
    const url = `${this.options.domain}/passengers/`;

    return this.request({
      url,
      method: "get",
      headers: {
        Accept: ACCEPT_HEADER_V3
      },
      qs: buildQueryStringsObject(options)
    }).then(response =>
      response.body.map((passenger: { id: any }) => ({
        ...passenger,
        id: Number(passenger.id)
      }))
    );
  }

  public getCreditCards(user_id: number): Promise<CreditCard[]> {
    const url = `${this.options.domain}/users/${user_id}/credit-cards`;

    return this.request({
      url: url,
      method: "get",
      headers: { Accept: ACCEPT_HEADER_V3 }
    }).then(response => {
      return response.body;
    });
  }

  public updateCreditCard(
    user_id: number,
    credit_card: CreditCard
  ): Promise<void> {
    const url = `${this.options.domain}/users/${user_id}/credit-cards/${credit_card.id}`;

    const { holder_name, expiry_month, expiry_year } = credit_card;

    return this.request({
      url: url,
      method: "put",
      headers: { Accept: ACCEPT_HEADER_V3 },
      data: {
        merchant_account: credit_card.merchant,
        holder_name,
        expiry_month,
        expiry_year
      }
    }).then(() => {});
  }

  public deleteCreditCard(user_id: number, credit_card: CreditCard) {
    const url = `${this.options.domain}/users/${user_id}/credit-cards/${credit_card.id}`;

    return this.request({
      url: url,
      method: "delete",
      headers: { Accept: ACCEPT_HEADER_V3 },
      data: {
        merchant_account: credit_card.merchant
      }
    }).then(response => {
      return response.status_code;
    });
  }

  public addPassenger(new_passenger: {
    first_name: string;
    last_name: string;
    gender?: PassengerGender;
  }) {
    const url = `${this.options.domain}/passengers/`;

    return this.request({
      url: url,
      method: "post",
      data: new_passenger,
      headers: {
        Accept: ACCEPT_HEADER_V3
      }
    }).then(response => {
      return response.body;
    });
  }

  public updatePassenger(updated_passenger: { id: string | number }) {
    const url = `${this.options.domain}/passengers/${updated_passenger.id}`;

    return this.request({
      url: url,
      method: "put",
      data: updated_passenger,
      headers: {
        Accept: ACCEPT_HEADER_V3
      }
    }).then(response => {
      return response.body;
    });
  }

  public deletePassenger(passenger_to_delete: { id: string | number }) {
    const url = `${this.options.domain}/passengers/${passenger_to_delete.id}`;

    return this.request({
      url: url,
      method: "delete",
      headers: {
        Accept: ACCEPT_HEADER_V3
      }
    }).then(response => {
      return response.status_code;
    });
  }

  public getUserPurchases(user_id: number): Promise<NapiV2Purchase[]> {
    const state: NapiRawState = "booked";
    const url = `${this.options.domain}/users/${user_id}/purchases`;

    return this.request({
      url,
      method: "get",
      qs: { state },
      headers: {
        Accept: ACCEPT_HEADER_V3
      }
    }).then(response => {
      return response.body;
    });
  }

  public fetchUserBillingAddress(
    user?: NapiUser | undefined
  ): Promise<UserBillingAddress | null> {
    if (!user || !user.id) {
      return Promise.resolve(null);
    }

    return this.request({
      url: `${this.options.domain}/users/${user.id}/billing-address`,
      method: "get",
      headers: { Accept: ACCEPT_HEADER_V3 }
    })
      .then(response => response.body)
      .catch(err => {
        const status_code: number = err.response ? err.response.status : 0;
        if (status_code !== 404) {
          sentry.captureException(err, {
            tags: { request: "get-users-billing-address" }
          });
        }
        return null;
      });
  }

  public saveUserBillingAddress(
    user_id: number,
    billing_address: UserBillingAddress
  ): Promise<UserBillingAddress> {
    return this.request({
      url: `${this.options.domain}/users/${user_id}/billing-address`,
      method: "put",
      data: billing_address,
      headers: { Accept: ACCEPT_HEADER_V3 }
    }).then(response => response.body);
  }

  public deleteUserBillingAddress(user_id: number): Promise<void> {
    return this.request({
      url: `${this.options.domain}/users/${user_id}/billing-address`,
      method: "delete",
      headers: { Accept: ACCEPT_HEADER_V3 }
    }).then(response => response.body);
  }

  public getCreditsActivity(year: number): Promise<CreditsActivity[]> {
    return this.request({
      url: this.options.domain + "/loyalty-profiles/credits-history",
      headers: { Accept: ACCEPT_HEADER_V3 },
      qs: { year }
    }).then(response => response.body);
  }

  public async pollCartV2(id: string): Promise<CartPollResponse | undefined> {
    return this.requestV2WithPolling(
      `/carts/${id}`,
      {
        locale: this.options.locale,
        currency: this.options.currency
      },
      CART_RETRY_CONFIGURATION.retries
    );
  }

  public async requestV2WithPolling<P extends CacheStateResponse>(
    initial_poll_url: string,
    options = {},
    retries: number
  ): Promise<P | undefined> {
    let next_poll_url = `${this.options.domain}${initial_poll_url}`;
    while (next_poll_url) {
      try {
        const res = await this.requestForPollingV2<P>(
          next_poll_url,
          options,
          retries
        );
        const { links, interval } = res.metadata;
        const { poll, poll_restart } = links;
        if (poll_restart) {
          throw new OutdatedPollingUrlError(poll_restart);
        }

        next_poll_url = poll ? `${this.options.domain}${poll}` : "";

        if (next_poll_url) {
          await new Promise(done => setTimeout(done, interval));
        } else {
          return res;
        }
      } catch (err) {
        if (err instanceof OutdatedPollingUrlError) {
          throw err;
        }
        throw toMaybeApiError(err);
      }
    }
  }

  private requestForPollingV2<P extends CacheStateResponse>(
    poll_url: string,
    options: {},
    retries: number
  ): Promise<P> {
    const qs = {
      ...buildQueryStringsObject(options),
      // Add timestamp to force IE not to cache response when polling
      ts: new Date().getTime()
    };

    const request_options = {
      url: poll_url,
      method: "get",
      qs,
      default_params: false,
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      },
      options: {
        // Ref: https://github.com/busbud/public-website/pull/13024#discussion_r729167478
        request_id_separator: "--"
      },
      "axios-retry": { retries },
      timeout: 30000
    };

    return this.request(request_options).then(res => res.body);
  }

  /**
   * @param {string} cart_id
   * @param {{[key: string]: any}} [options]
   * @returns {Promise<Napi.CartResponse>}
   */
  public getCart(cart_id: string, options = {}) {
    const url = `${this.options.domain}/cart/${cart_id}`;

    const request_options = {
      url: url,
      method: "get",
      qs: buildQueryStringsObject(options)
    };

    return this.request(request_options).then(
      ({ body: { data, related } }) => ({ cart: data, related })
    );
  }

  public updateCartTripV2(
    cart_id: string,
    trip_id: string,
    data: UpdateCartTripPayload,
    params: UpdateCartTripParams = {}
  ): Promise<NapiV2CartTrip> {
    const search_params = new URLSearchParams();

    if (params.save_passenger_question_answers) {
      search_params.append("save_passenger_question_answers", "true");
    }

    const url = `${this.options.domain}/carts/${cart_id}/trips/${trip_id}?${search_params.toString()}`;

    return this.request({
      url,
      method: "put",
      data,
      "axios-retry": CART_RETRY_CONFIGURATION,
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      }
    }).then(response => {
      return response.body;
    });
  }

  public updateCartPurchaser(
    cart_id: string,
    purchaser: Purchaser
  ): Promise<Purchaser> {
    const url = `${this.options.domain}/carts/${cart_id}/purchaser`;

    return this.request({
      url,
      method: "put",
      data: { purchaser },
      "axios-retry": CART_RETRY_CONFIGURATION,
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      }
    }).then(response => {
      return response.body;
    });
  }

  public updateCartBillingAddress(
    cart_id: string,
    billing_address: NapiV2Address
  ): Promise<NapiV2Address> {
    const url = `${this.options.domain}/carts/${cart_id}/billing-address`;

    return this.request({
      url,
      method: "put",
      data: { billing_address },
      "axios-retry": CART_RETRY_CONFIGURATION,
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      }
    }).then(response => {
      return response.body;
    });
  }

  public updateCart(
    cart_id: string,
    data: UpdateCartParams & CartContext
  ): Promise<{ cart: any; related: any }> {
    var url = this.options.domain + "/cart/" + cart_id;

    return this.request({
      url: url,
      method: "put",
      data: data
    }).then(response => {
      return {
        cart: response.body.data,
        related: response.body.related
      };
    });
  }

  public applyDiscountCodeToCart(
    cart_id: string,
    discount_code: string,
    cart_context: CartContext
  ) {
    return this.request({
      url: `${this.options.domain}/cart/${cart_id}/discount`,
      method: "put",
      data: {
        discount_code: discount_code,
        ...cart_context
      }
    }).then(response => response.body.data);
  }

  public applyCreditRedeemToCart(cart_id: string, cart_context: CartContext) {
    return this.request({
      url: `${this.options.domain}/cart/${cart_id}/redeem`,
      method: "post",
      headers: { Accept: ACCEPT_HEADER_V3 },
      data: {
        ...cart_context
      }
    }).then(response => response.body.data);
  }

  public resetCreditRedeemFromCart(cart_id: string, cart_context: CartContext) {
    return this.request({
      url: `${this.options.domain}/cart/${cart_id}/unredeem`,
      method: "post",
      headers: { Accept: ACCEPT_HEADER_V3 },
      data: {
        ...cart_context
      }
    }).then(response => response.body.data);
  }

  public validateDiscountCodeForSearch(
    search: SearchInfo
  ): Promise<ValidateDiscountCodeResponse> {
    if (search && !search.discount_code) {
      return Promise.resolve({ valid: true });
    }

    let url: string;

    if (search.return_date) {
      url = `${this.options.domain}/x-departures/{origin}/{destination}/{outbound_date}/{return_date}/discount-code`;
    } else {
      url = `${this.options.domain}/x-departures/{origin}/{destination}/{outbound_date}/discount-code`;
    }

    return this.request({
      url: URIExpand(url, {
        origin: search.origin,
        destination: search.destination,
        outbound_date: search.outbound_date,
        return_date: search.return_date
      }).toString(),
      qs: {
        discount_code: search.discount_code
      },
      method: "get"
    })
      .then(response => {
        return response.body;
      })
      .catch(() => {
        return Promise.resolve({ valid: false });
      });
  }

  public resetCartWithoutDiscountCode(
    cart_id: string,
    cart_context: CartContext
  ) {
    return this.request({
      url: `${this.options.domain}/cart/${cart_id}/discount`,
      method: "delete",
      qs: cart_context
    }).then(response => response.body.data);
  }

  public refreshCartPrice(
    cart_id: string
  ): Promise<{ cart: any; related: any }> {
    var url = this.options.domain + "/cart/" + cart_id + "/refresh-price";

    return this.request({
      url: url,
      method: "post"
    }).then(response => {
      return {
        cart: response.body.data,
        related: response.body.related
      };
    });
  }

  // Try to find an existing Purchase for a given Cart.
  public getCartPurchase(cart_id: string): Promise<CartPurchase | null> {
    const url = `${this.options.domain}/cart/${cart_id}/purchase`;

    const request_options = {
      url: url,
      method: "get"
    };

    return this.request(request_options).then(response => {
      if (response.body) {
        return {
          purchase_id: response.body.purchase_id,
          purchase_uuid: response.body.purchase_uuid,
          user_id: response.body.user_id
        };
      }
      return null;
    });
  }

  public getPurchaseByEmailCreditCardAndDepartureDate(
    email: string,
    cc_last_4: string,
    departure_date: string
  ): Promise<Partial<NapiPurchase>> {
    return this.request({
      url: `${this.options.domain}/purchases/search-by-attributes`,
      method: "get",
      qs: { email, cc_last_4, departure_date }
    }).then(response => response.body);
  }

  public purchaseCart(
    cart_id: string,
    data: PurchaseCartParams | PartnerPurchaseCartParams,
    currency?: string
  ): Promise<CartPurchase> {
    const url = this.options.domain + "/cart/" + cart_id + "/purchase";
    const request_options = {
      url: url,
      method: "post",
      data: data,
      timeout: CART_PURCHASE_TIMEOUT
    };

    if (currency) {
      // @ts-expect-error
      request_options.qs = {
        currency: currency
      };
    }

    return this.request(request_options).then(response => {
      return {
        purchase_id: response.body.purchase_id,
        purchase_uuid: response.body.purchase_uuid,
        user_id: response.body.user_id
      };
    });
  }

  public getPurchaseStatus(
    purchase_id: number,
    purchase_uuid: string
  ): Promise<NapiPurchaseStatusResponse> {
    const url = this.options.domain + "/purchases/" + purchase_id + "/status";

    const qs = {
      purchase_uuid: purchase_uuid,
      // Add timestamp to force IE not to cache response when polling
      ts: new Date().getTime()
    };

    return this.request({
      url: url,
      headers: {
        // Don't cache response when polling
        "Cache-Control": "no-cache"
      },
      method: "get",
      "axios-retry": CART_RETRY_CONFIGURATION,
      qs: qs
    }).then(response => {
      return {
        state: response.body.state,
        error: response.body.error,
        redirection_setup: response.body.redirection_setup,
        threeds2_setup: response.body.threeds2_setup,
        action_setup: response.body.action_setup
      };
    });
  }

  /**
   * @deprecated use getPurchaseV2
   * @param purchase_id number | string | null
   * @param purchase_uuid string | null | undefined
   * @returns Promise<NapiPurchaseResponse>
   */
  public getPurchase(
    purchase_id: number | string | null,
    purchase_uuid?: string | null
  ): Promise<NapiPurchaseResponse | null> {
    if (!purchase_id) {
      return Promise.resolve(null);
    }

    const url = this.options.domain + "/purchases/" + purchase_id;

    const qs: any = {};
    if (purchase_uuid) {
      qs.purchase_uuid = purchase_uuid;
    }

    return this.request({
      url: url,
      method: "get",
      qs: qs
    }).then(response => {
      return response.body;
    });
  }

  public getPurchaseV2(
    purchase_id: number | string,
    purchase_uuid?: string
  ): Promise<NapiV2PurchaseDetails> {
    const url = this.options.domain + "/purchases/" + purchase_id;

    const qs: any = {};
    if (purchase_uuid) {
      qs.purchase_uuid = purchase_uuid;
    }

    return this.request({
      url: url,
      method: "get",
      qs: qs,
      headers: { Accept: ACCEPT_HEADER_V3 }
    }).then(response => {
      return response.body;
    });
  }

  public getPurchaseReceipt(
    purchase_id: string,
    purchase_uuid: string | qs.ParsedQs | string[] | qs.ParsedQs[] | undefined,
    extension: string | qs.ParsedQs | string[] | qs.ParsedQs[] | undefined
  ) {
    return this.request({
      url: `${this.options.domain}/purchases/${purchase_id}/customer-receipt`,
      method: "get",
      qs: {
        purchase_uuid,
        extension
      },
      responseType: "arraybuffer" // otherwise axios sets contenttype json by default and it breaks pdf
    });
  }

  public findPurchase(
    reference: string | string[] | qs.ParsedQs | qs.ParsedQs[],
    email: string | qs.ParsedQs | string[] | qs.ParsedQs[]
  ) {
    const url = this.options.domain + "/purchases/";

    const qs = {
      reference,
      email
    };

    return this.request({
      url: url,
      method: "get",
      qs: qs
    }).then(response => {
      return response.body;
    });
  }

  public resendPurchaseConfirmationEmail(
    purchase_id: number,
    purchase_uuid: string,
    email: string
  ) {
    const url =
      this.options.domain +
      "/purchases/" +
      URI.encode(String(purchase_id)) +
      "/resend-tickets" +
      "?purchase_uuid=" +
      URI.encode(purchase_uuid);

    const data = { email };

    return this.request({
      url: url,
      method: "post",
      data
    }).then(response => {
      return response.body;
    });
  }

  public getPurchaseCharges(
    purchase_id: number,
    purchase_uuid?: string
  ): Promise<NapiPurchaseChargesResponse> {
    const url = this.options.domain + "/purchases/" + purchase_id + "/charges";

    const qs: any = {};
    if (purchase_uuid) {
      qs.purchase_uuid = purchase_uuid;
    }

    return this.request({
      url: url,
      method: "get",
      qs: qs
    }).then(response => {
      return response.body;
    });
  }

  public getProfiles(qs: {
    promoted?: boolean;
    published?: boolean;
    with_logo_url?: boolean;
    url_forms?: string;
    order?: "visits" | "url_forms" | "country_visits";
    order_direction?: "ASC" | "DESC";
    limit?: number;
    offset?: number;
    country_code2?: string;
    vehicle_type?: VehicleType | readonly VehicleType[];
    ids?: string;
  }): Promise<GetProfilesResponse> {
    const url = this.options.domain + "/profiles";
    return this.request({
      url: url,
      qs: qs,
      default_params: false,
      method: "get"
    }).then(response => response.body);
  }

  public getProfile(
    id_or_url_form: string | number,
    params?: { with_amenities?: true }
  ): Promise<NapiPartnerProfileResponse | null> {
    if (!id_or_url_form) {
      return Promise.resolve(null);
    }
    const url = `${this.options.domain}/profiles/${id_or_url_form}`;

    return this.request({
      url: url,
      method: "get",
      qs: params,
      default_params: false,
      http_caching_enabled: true
    })
      .then(({ body }) => body.profile)
      .catch(e => {
        if (e?.response?.status === 404) {
          return null;
        }
        throw e;
      });
  }

  /**
   * Gets the list of operators linked to the profile.
   *
   * @param identifier the url_form or id of the profile.
   * @param qs
   * @param qs.amenities_published
   * @param qs.exclude_inactive_sources
   * @returns
   */
  public getProfileOperators(
    identifier: string | number,
    qs: { amenities_published: boolean; exclude_inactive_sources: boolean }
  ): Promise<{ body: { operators: NapiOperator[] } }> {
    const url = this.options.domain + `/profiles/${identifier}/operators`;

    return this.request({
      url: url,
      qs: qs,
      method: "get",
      http_caching_enabled: true
    });
  }

  private getFareTypes(params: {
    profile_url_form: string;
    fare_id: string;
  }): Promise<FareType[]> {
    const request_options = {
      url: `${this.options.domain}/fare-types`,
      method: "GET",
      qs: buildQueryStringsObject(params)
    };

    return this.request(request_options).then(response => response.body);
  }

  public getDefaultFareType(params: {
    profile_url_form: string;
  }): Promise<FareType[]> {
    return this.getFareTypes({ ...params, fare_id: "default" }).then(
      fare_types => fare_types.filter(f => f.fare_id === "default")
    );
  }

  public initDepartures(
    search: SearchInfo
  ): Promise<NapiFetchDeparturesResponse> {
    let url: string;

    if (search.return_date) {
      url =
        this.options.domain +
        "/x-departures/{origin}/{destination}/{outbound_date}/{return_date}";
    } else {
      url =
        this.options.domain +
        "/x-departures/{origin}/{destination}/{outbound_date}";
    }

    return this.request({
      url: URIExpand(url, {
        origin: search.origin,
        destination: search.destination,
        outbound_date: search.outbound_date,
        return_date: search.return_date
      }).toString(),
      qs: {
        adult: search.adult,
        child: search.child,
        senior: search.senior,
        adult_wheelchair: search.adult_wheelchair,
        child_wheelchair: search.child_wheelchair,
        senior_wheelchair: search.senior_wheelchair,
        child_ages: search.child_ages && search.child_ages.join(","),
        senior_ages: search.senior_ages && search.senior_ages.join(","),
        child_wheelchair_ages:
          search.child_wheelchair_ages &&
          search.child_wheelchair_ages.join(","),
        senior_wheelchair_ages:
          search.senior_wheelchair_ages &&
          search.senior_wheelchair_ages.join(","),
        ...getDiscountsQueryAttributes(search),
        discount_code: search.discount_code,
        include_sold_out: true
      },
      method: "get"
    }).then(response => {
      return response.body;
    });
  }

  public pollDeparturesReturn(
    search: SearchInfo,
    index: number
  ): Promise<PollDeparturesReturnResponse> {
    const url =
      this.options.domain +
      "/x-departures/{origin}/{destination}/{outbound_date}/{return_date}/{outbound_id}/poll";
    const poll_index = index || 0;

    return this.request({
      url: URIExpand(url, {
        origin: search.origin,
        destination: search.destination,
        outbound_date: search.outbound_date,
        return_date: search.return_date,
        outbound_id: search.outbound_id
      }).toString(),
      qs: {
        adult: search.adult,
        child: search.child,
        senior: search.senior,
        adult_wheelchair: search.adult_wheelchair,
        child_wheelchair: search.child_wheelchair,
        senior_wheelchair: search.senior_wheelchair,
        child_ages: search.child_ages && search.child_ages.join(","),
        senior_ages: search.senior_ages && search.senior_ages.join(","),
        child_wheelchair_ages:
          search.child_wheelchair_ages &&
          search.child_wheelchair_ages.join(","),
        senior_wheelchair_ages:
          search.senior_wheelchair_ages &&
          search.senior_wheelchair_ages.join(","),
        ...getDiscountsQueryAttributes(search),
        discount_code: search.discount_code,
        index: poll_index,
        include_sold_out: true
      },
      method: "get"
    }).then(response => {
      return response.body;
    });
  }

  /**
   * Gets alternative routes for a search.
   *
   * @param search
   */
  public getAlternativeRoutes(
    search: SearchInfo
  ): Promise<PollService.API.AlternativeRoutesResponse> {
    return this.request({
      url: `${this.options.domain}/metadata/alternative-routes`,
      qs: {
        origin_geohash: search.origin,
        destination_geohash: search.destination,
        outbound_date: search.outbound_date,
        return_date: search.return_date,
        lang: this.options.lang
      },
      method: "get"
    }).then(response => response.body);
  }

  public getBookingStatus(
    booking_id: string
  ): Promise<{ status: NapiBookingStatus; error?: any }> {
    const url = this.options.domain + "/bookings/{booking_id}/status";

    return this.request({
      url: URIExpand(url, {
        booking_id: booking_id
      }).toString(),
      method: "get"
    }).then(response => {
      return {
        status: response.body.status,
        error: response.body.error
      };
    });
  }

  /**
   * Try to find an existing Purchase for a given Booking.
   *
   * @param booking_id
   */
  public getBookingPurchase(booking_id: string): Promise<CartPurchase | null> {
    const url = `${this.options.domain}/bookings/${booking_id}/purchase`;

    const request_options = {
      url: url,
      method: "get"
    };

    return this.request(request_options).then(response => {
      if (response.body) {
        return {
          purchase_id: response.body.purchase_id,
          purchase_uuid: response.body.purchase_uuid,
          user_id: response.body.user_id
        };
      }
      return null;
    });
  }

  public updateUser(
    user_id: number,
    data:
      | {
          first_name: string;
          last_name: string;
          id: number;
          is_dirty: boolean;
          full_name: string;
        }
      | {
          email_opt_in: boolean;
          id: number;
          first_name: string;
          last_name: string;
          email: string;
          phone_number: string | null;
          locale: string;
          verified_at: string | null;
          claimed_at: string | null;
          created_at: string;
          signin_method: string;
          gender?: "male" | "female" | "other" | null | undefined;
          birthdate?: string | null | undefined;
        }
      | {
          locale: string;
          id: number;
          first_name: string;
          last_name: string;
          email: string;
          phone_number: string | null;
          email_opt_in: boolean;
          verified_at: string | null;
          claimed_at: string | null;
          created_at: string;
          signin_method: string;
          gender?: "male" | "female" | "other" | null | undefined;
          birthdate?: string | null | undefined;
        }
  ) {
    var url = this.options.domain + "/users/{user_id}";

    return this.request({
      url: URIExpand(url, { user_id: `${user_id}` }).toString(),
      data,
      headers: {
        Accept: ACCEPT_HEADER_V3
      },
      method: "put"
    });
  }

  public updateUserEmail(user_id: number, email: string, password: string) {
    var url = this.options.domain + "/users/{user_id}/update-email";

    return this.request({
      url: URIExpand(url, { user_id: `${user_id}` }).toString(),
      data: {
        email,
        password
      },
      headers: {
        Accept: ACCEPT_HEADER_V3
      },
      method: "put"
    }).then(response => {
      return response.body;
    });
  }

  public requestPasswordReset(data: { locale: string; email: string }) {
    var url = this.options.domain + "/auth/reset_password";

    return this.request({
      method: "post",
      url: url,
      data,
      headers: {
        Accept: ACCEPT_HEADER_V3
      }
    });
  }

  public changePassword(token: string, password: string) {
    const url = this.options.domain + "/auth/change-password";

    return this.request({
      method: "post",
      url: url,
      headers: {
        Accept: ACCEPT_HEADER_V3
      },
      data: {
        token,
        password
      }
    }).then(response => {
      const auth_result = response.body.data;
      const user = response.body.related.users[auth_result.user_id];

      return {
        auth_result: auth_result,
        user: user
      };
    });
  }

  public changeUserPassword(
    user_id: number,
    current_password: string,
    new_password: string
  ) {
    return this.request({
      method: "put",
      url: `${this.options.domain}/users/${user_id}/set_password`,
      headers: {
        Accept: ACCEPT_HEADER_V3
      },
      data: {
        current_password,
        new_password
      }
    }).then(response => {
      const auth_result = response.body.data;
      const user = response.body.related.users[auth_result.user_id];

      return {
        auth_result,
        user
      };
    });
  }

  /**
   *
   * This method is "safe" as it returns `null` in case of 404.
   * `getRoute` just throws.
   *
   * @param origin
   * @param destination
   * @param options
   */
  public getRoute(
    origin: string,
    destination: string,
    options?: RouteRequestOptions
  ): Promise<NapiRoute | null> {
    const url = this.options.domain + "/routes/{origin}/{destination}";

    let qs = options;
    // Add query param to skip fastly cache.
    if (
      this.options.experiments &&
      ["skip_direct_departures", "1"].includes(
        // @ts-expect-error TS2769 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        this.options.experiments.interconnection_worldwide
      )
    ) {
      qs = Object.assign({}, options, {
        iw: "true",
        skip_direct_departures:
          this.options.experiments.interconnection_worldwide ===
            "skip_direct_departures" && "true"
      });
    }

    return this.request({
      url: URIExpand(url, {
        origin: origin,
        destination: destination
      }).toString(),
      qs,
      method: "get"
    })
      .then(response => response.body)
      .catch(err => {
        const status_code: number = err.response ? err.response.status : 0;
        if (status_code === 404) {
          return null;
        }
        throw err;
      });
  }

  public getRouteSources(
    origin: string,
    destination: string,
    timeout: number
  ): Promise<NapiSource[]> {
    const url = `${this.options.domain}/routes/{origin}/{destination}/sources`;

    return this.request({
      url: URIExpand(url, {
        origin: origin,
        destination: destination
      }).toString(),
      method: "get",
      timeout
    }).then(response => response.body.data);
  }

  /*
   * Gets outages for a given source
   * NOTE: this endpoint requires special permissions and therefore a privileged token
   */
  public getSourceOutageStatus(
    source_id: number,
    limit: number = 50,
    include_closed: boolean = false
  ): Promise<SourceStatus> {
    const url = `${this.options.domain}/source-outages`;

    // Limit to 50 outages as this is the max supported on NAPI
    if (limit > 50) {
      limit = 50;
    }

    return this.request({
      url,
      method: "get",
      default_params: false,
      qs: { source_id, limit, not_ended: !include_closed }
    }).then(response => response.body.data);
  }

  public getStatsCities(opts: {
    country_code2: string;
    limit: number;
  }): Promise<{
    body: StatsCity[];
  }> {
    const url = this.options.domain + "/stats/cities";
    return this.request({
      url: url,
      qs: opts,
      method: "get"
    });
  }

  public getCities(query: {
    geohashes: string[];
  }): Promise<GrandCentral.API.City[]> {
    const url = this.options.domain + "/cities";
    const qs = {
      // Sorting the geohashes should improve the cachability of that call
      geohashes: [...query.geohashes].sort().join(","),
      lang: this.options.lang
    };

    return this.request({
      url: url,
      qs: qs,
      default_params: false
    }).then(({ body }) => body);
  }

  public getCity(
    city_id: string
  ): Promise<GrandCentral.API.CityWithRegion | null> {
    const url = this.options.domain + "/cities/" + city_id;

    return this.request({
      url: url,
      method: "get",
      default_params: false,
      qs: {
        locale: this.options.locale
      }
    })
      .then(response => response.body)
      .catch(err => {
        const status_code: number = err.response ? err.response.status : 0;
        if (status_code === 404) {
          return null;
        }
        throw err;
      });
  }

  public getCityMappings(
    city_id: string,
    query: { third_party: CityMappingThirdParties } | undefined
  ): Promise<CityMappingResponse | {}> {
    const url = `${this.options.domain}/city-mappings/${city_id}`;

    const request_config: HTTPClientConfig = {
      url: url,
      method: "get"
    };

    if (query && query.third_party) {
      request_config.qs = {
        third_party: query.third_party
      };
    }
    return this.request(request_config)
      .then(({ body }) => body)
      .catch(err => {
        const status_code: number = err.response ? err.response.status : 0;
        if (status_code === 404) {
          return {};
        }
        sentryFacade.captureException(err, {
          tags: { third_party: query?.third_party ?? "not-provided", city_id }
        });
        return {};
      });
  }

  public getCountry(
    country_code2: string
  ): Promise<GrandCentral.API.Country | null> {
    const url =
      this.options.domain + "/countries/" + country_code2.toUpperCase();

    return this.request({
      url: url,
      method: "get",
      default_params: false,
      qs: {
        locale: this.options.locale
      }
    })
      .then(({ body }) => body.country)
      .catch(err => {
        const status_code: number = err.response ? err.response.status : 0;
        if (status_code === 404) {
          return null;
        }
        throw err;
      });
  }

  public getFeaturedCountries(
    country_code2: string,
    limit: number
  ): Promise<FeaturedCountry[]> {
    const url = `${this.options.domain}/countries/${country_code2}/popular-countries`;

    return this.request({
      url: url,
      method: "get",
      default_params: false,
      qs: {
        lang: this.options.lang,
        limit
      }
    }).then(({ body }) => body);
  }

  public getLocationStation(
    location_id: number | string
  ): Promise<GetLocationStationResponse | null> {
    const url = `${this.options.domain}/locations/${location_id}/station`;

    return this.request({
      url: url,
      method: "get"
    })
      .then(response => {
        return response.body;
      })
      .catch(e => {
        if (e?.response?.status === 404) {
          return null;
        }
        throw e;
      });
  }

  public async getLocation(
    location_id: string
  ): Promise<{ location: NapiLocation; city: NapiCity }> {
    const url = this.options.domain + "/locations/" + location_id;

    return this.request({
      url: url,
      method: "get"
    }).then(response => {
      return response.body;
    });
  }

  public async getGeoEntityByGeoId(geo_id: string): Promise<GeoEntity> {
    const url = this.options.domain + "/geo/geo-entities/" + geo_id;

    return this.request({
      url: url,
      method: "get"
    }).then(response => {
      return response.body;
    });
  }

  public emailSignUp(params: SignupParams): Promise<SignupResponse> {
    return this.request({
      url: this.options.domain + "/auth/signup",
      method: "POST",
      headers: {
        Accept: ACCEPT_HEADER_V3
      },
      data: params
    }).then(response => {
      const auth_result: AuthResultVerified = response.body.data;
      return {
        auth_result,
        user: response.body.related.users[auth_result.user_id]
      };
    });
  }

  public emailSignIn(email: string, password: string): Promise<LoginResponse> {
    return this.request({
      url: this.options.domain + "/auth/signin",
      method: "POST",
      headers: {
        Accept: ACCEPT_HEADER_V3
      },
      data: { email, password }
    }).then(response => {
      const auth_result = response.body.data;
      const user = response.body.related.users[auth_result.user_id];

      return {
        auth_result,
        user
      };
    });
  }

  /**
   * @returns {Promise<void>}
   */
  public logout() {
    return this.request({
      url: this.options.domain + "/auth/logout",
      method: "POST",
      headers: { Accept: ACCEPT_HEADER_V3 }
    }).then(() => {
      return;
    });
  }

  /*
   * Using new API version v2
   * /v2/auth/apple
   */
  public appleAuth(params: unknown) {
    return this.request({
      url: this.options.domain + "/auth/apple",
      method: "POST",
      headers: { Accept: ACCEPT_HEADER_V3 },
      data: params
    }).then(response => {
      const auth_result: AuthResultVerified = response.body.data;
      return {
        auth_result,
        user: response.body.related.users[auth_result.user_id]
      };
    });
  }

  /*
   * Using new API version v2
   * /v2/auth/google
   */
  public googleAuth(params: unknown) {
    return this.request({
      url: this.options.domain + "/auth/google",
      method: "POST",
      headers: { Accept: ACCEPT_HEADER_V3 },
      data: params
    }).then(response => {
      const auth_result: AuthResultVerified = response.body.data;
      return {
        auth_result,
        user: response.body.related.users[auth_result.user_id]
      };
    });
  }

  /*
   * Using new API version v2
   * /v2/auth/facebook
   */
  public fbAuth(params: unknown) {
    return this.request({
      url: this.options.domain + "/auth/facebook",
      headers: { Accept: ACCEPT_HEADER_V3 },
      method: "POST",
      data: params
    }).then(response => {
      const auth_result: AuthResultVerified = response.body.data;
      return {
        auth_result,
        user: response.body.related.users[auth_result.user_id]
      };
    });
  }

  public verifyUser(token: string): Promise<void> {
    return this.request({
      url: this.options.domain + "/auth/verify-user",
      method: "POST",
      data: {
        token
      },
      headers: { Accept: ACCEPT_HEADER_V3 }
    }).then(() => {
      return;
    });
  }

  public resendVerificationEmail(email: string) {
    return this.request({
      url: this.options.domain + "/auth/resend-verification",
      method: "POST",
      data: {
        email: email
      },
      headers: { Accept: ACCEPT_HEADER_V3 }
    });
  }

  public getSearchDetails(
    search: SearchInfo,
    direction: string,
    departure_id: string,
    timeout?: number
  ): Promise<NapiDepartureDetailsResponse> {
    /** @type {string} */
    let url: string;

    if (search.return_date) {
      url =
        this.options.domain +
        "/x-departures/{origin}/{destination}/{outbound_date}/{return_date}/{direction}/{departure_id}/details";
    } else {
      url =
        this.options.domain +
        "/x-departures/{origin}/{destination}/{outbound_date}/{direction}/{departure_id}/details";
    }

    return this.request({
      url: URIExpand(url, {
        origin: search.origin,
        destination: search.destination,
        outbound_date: search.outbound_date,
        return_date: search.return_date,
        direction: direction,
        departure_id: departure_id
      }).toString(),
      qs: {
        adult: search.adult,
        child: search.child,
        senior: search.senior,
        adult_wheelchair: search.adult_wheelchair,
        child_wheelchair: search.child_wheelchair,
        senior_wheelchair: search.senior_wheelchair,
        child_ages: search.child_ages && search.child_ages.join(","),
        senior_ages: search.senior_ages && search.senior_ages.join(","),
        child_wheelchair_ages:
          search.child_wheelchair_ages &&
          search.child_wheelchair_ages.join(","),
        senior_wheelchair_ages:
          search.senior_wheelchair_ages &&
          search.senior_wheelchair_ages.join(","),
        ...getDiscountsQueryAttributes(search),
        discount_code: search.discount_code
      },
      method: "get",
      timeout
    }).then(response => {
      return response.body;
    });
  }

  public getDepartureDetails(
    search: SearchInfo,
    direction: string,
    departure_id: string
  ): Promise<NapiDepartureDetailsResponse> {
    /** @type {string} */
    let url: string;

    if (search.return_date) {
      url =
        this.options.domain +
        "/x-departures/{origin}/{destination}/{outbound_date}/{return_date}/{direction}/{departure_id}/booking_details";
    } else {
      url =
        this.options.domain +
        "/x-departures/{origin}/{destination}/{outbound_date}/{direction}/{departure_id}/booking_details";
    }

    return this.request({
      url: URIExpand(url, {
        origin: search.origin,
        destination: search.destination,
        outbound_date: search.outbound_date,
        return_date: search.return_date,
        direction: direction,
        departure_id: departure_id
      }).toString(),
      qs: {
        adult: search.adult,
        child: search.child,
        senior: search.senior,
        adult_wheelchair: search.adult_wheelchair,
        child_wheelchair: search.child_wheelchair,
        senior_wheelchair: search.senior_wheelchair,
        child_ages: search.child_ages && search.child_ages.join(","),
        senior_ages: search.senior_ages && search.senior_ages.join(","),
        child_wheelchair_ages:
          search.child_wheelchair_ages &&
          search.child_wheelchair_ages.join(","),
        senior_wheelchair_ages:
          search.senior_wheelchair_ages &&
          search.senior_wheelchair_ages.join(","),
        ...getDiscountsQueryAttributes(search),
        discount_code: search.discount_code
      },
      method: "get"
    }).then(response => {
      return response.body;
    });
  }

  public getDeparture(
    search: SearchInfo,
    direction: "outbound",
    departure_id: string,
    call_source = ""
  ) {
    let url: string;

    if (search.return_date) {
      url =
        this.options.domain +
        "/x-departures/{origin}/{destination}/{outbound_date}/{return_date}/{direction}/{departure_id}";
    } else {
      url =
        this.options.domain +
        "/x-departures/{origin}/{destination}/{outbound_date}/{direction}/{departure_id}";
    }

    return this.request({
      url: URIExpand(url, {
        origin: search.origin,
        destination: search.destination,
        outbound_date: search.outbound_date,
        return_date: search.return_date,
        direction: direction,
        departure_id: departure_id
      }).toString(),
      qs: {
        adult: search.adult,
        child: search.child,
        senior: search.senior,
        adult_wheelchair: search.adult_wheelchair,
        child_wheelchair: search.child_wheelchair,
        senior_wheelchair: search.senior_wheelchair,
        child_ages: search.child_ages && search.child_ages.join(","),
        senior_ages: search.senior_ages && search.senior_ages.join(","),
        child_wheelchair_ages:
          search.child_wheelchair_ages &&
          search.child_wheelchair_ages.join(","),
        senior_wheelchair_ages:
          search.senior_wheelchair_ages &&
          search.senior_wheelchair_ages.join(","),
        ...getDiscountsQueryAttributes(search),
        discount_code: search.discount_code,
        call_source
      },
      method: "get"
    });
  }

  public async getPurchaseCancellationOptions(
    purchase_id: number,
    purchase_uuid?: string
  ): Promise<NapiPurchaseCancellationOptions> {
    const url =
      this.options.domain + "/purchases/{purchase_id}/cancellation-options";

    const path_params: ListCancellationPathParams = {
      purchase_id: String(purchase_id)
    };

    const qs: ListCancellationQueryString = {
      credit_types: ["busbud_credits", "original_payment_method", "voucher"],
      ...(purchase_uuid && { purchase_uuid })
    };

    const response = await this.request({
      url: URIExpand(url, path_params).toString(),
      method: "GET",
      qs,
      headers: { Accept: ACCEPT_HEADER_V3 }
    });

    return response.body;
  }

  public cancelPurchase(
    purchase_id: number,
    cancellation_options: NapiCancellationOption[],
    purchase_uuid?: string,
    refund_method: CreditType = "original_payment_method"
  ): Promise<NapiPurchaseCancellation> {
    const url = this.options.domain + "/purchases/{purchase_id}/cancellations";

    const cancellation_options_to_cancel = cancellation_options.reduce(
      (acc: PurchaseCancellationOption[], option) => {
        if (option.credit_types.includes(refund_method)) {
          const body_cancellation_option: PurchaseCancellationOption = {
            id: option.id,
            credit_type: refund_method
          };

          acc.push(body_cancellation_option);
        }

        return acc;
      },
      []
    );

    const path_params: PurchaseCancellationPathParams = {
      purchase_id: String(purchase_id)
    };

    const qs: PurchaseCancellationQueryString = {
      ...(purchase_uuid && { purchase_uuid })
    };

    const data: PurchaseCancellationBody = {
      cancellation_options: cancellation_options_to_cancel
    };

    return this.request({
      url: URIExpand(url, path_params).toString(),
      method: "POST",
      qs,
      headers: { Accept: ACCEPT_HEADER_V3 },
      data
    }).then(response => {
      return response.body;
    });
  }

  public getPublishedTravelReviews(params: {
    limit: number;
    unique_by_traveller_email: boolean;
    route_geohashes?: RouteGeohashes;
    preferred_length?: number;
    content_length_gte?: number;
    vehicle_type?: VehicleType;
    profile_id?: number;
    profile_ids?: number[];
    origin_location_ids?: number[];
    destination_location_ids?: number[];
    profile_url_form?: string;
    curated?: boolean;
  }): Promise<GrandCentral.API.TravelReview[]> {
    const url = this.options.domain + "/published-travel-reviews";

    // Use the "language group" for travel reviews requests by default
    // @ts-expect-error
    const language_group = Locales.supported_locales[this.options.locale].lang;
    const qs: any = {
      lang: language_group,
      limit: MAX_NUM_REVIEWS
    };

    if (params.route_geohashes) {
      qs.origin_geohash = params.route_geohashes.origin_geohash;
      qs.destination_geohash = params.route_geohashes.destination_geohash;
    }

    if (params.origin_location_ids?.length) {
      qs.origin_location_id = params.origin_location_ids;
    }

    if (params.destination_location_ids?.length) {
      qs.destination_location_id = params.destination_location_ids;
    }

    if (params.profile_id) {
      qs.profile_id = params.profile_id;
    }

    if (params.profile_ids) {
      qs.profile_ids = params.profile_ids.join(",");
    }

    if (params.profile_url_form) {
      qs.profile_url_form = params.profile_url_form;
      qs.with_profile_children = "true";
    }

    if (params.limit) {
      qs.limit = params.limit;
    }

    if (params.preferred_length) {
      qs.preferred_length = params.preferred_length;
    }

    if (params.content_length_gte) {
      qs.content_length_gte = params.content_length_gte;
    }

    if (params.vehicle_type) {
      qs.vehicle_type = params.vehicle_type;
    }

    if (params.unique_by_traveller_email) {
      qs.unique_by_traveller_email = "true";
    }

    if (typeof params.curated === "boolean") {
      qs.curated = params.curated;
    }

    return this.request({
      url,
      qs,
      default_params: false,
      method: "GET"
    })
      .then(response => response.body.data)
      .catch(err => {
        sentry.captureException(err, {
          tags: { request: "published_travel_reviews" }
        });
        return [];
      });
  }

  public getTravelReviewsStats(params: {
    route_geohashes?: RouteGeohashes;
    profile_id?: number;
    profile_ids?: number[];
    vehicle_type?: VehicleType;
    origin_location_ids?: number[];
    destination_location_ids?: number[];
    min_review_date?: string;
    operator_id?: string;
    profile_url_form?: string;
    with_ratings_breakdown?: boolean;
  }): Promise<GrandCentral.API.TravelReviewStats> {
    const url = this.options.domain + "/travel-reviews-stats";
    const qs: Record<string, string | number[] | number> = {};
    const EMPTY_STATS = {
      count_total: 0,
      count_for_lang: {},
      global_rating: 0,
      staff_rating: 0,
      timeliness_rating: 0,
      seats_rating: 0,
      bus_temperature_rating: 0,
      departure_location_rating: 0,
      cleanliness_rating: 0,
      wifi_rating: 0,
      power_outlets_rating: 0,
      value_for_money_rating: 0,
      ticket_access_rating: 0
    };

    if (params.route_geohashes) {
      qs.origin_geohash = params.route_geohashes.origin_geohash;
      qs.destination_geohash = params.route_geohashes.destination_geohash;
    }

    if (params.origin_location_ids?.length) {
      qs.origin_location_id = params.origin_location_ids;
    }

    if (params.destination_location_ids?.length) {
      qs.destination_location_id = params.destination_location_ids;
    }

    if (params.profile_id) {
      qs.profile_id = params.profile_id;
    }

    if (params.profile_ids) {
      qs.profile_ids = params.profile_ids.join(",");
    }

    if (params.profile_url_form) {
      qs.profile_url_form = params.profile_url_form;
      qs.with_profile_children = "true";
    }

    if (params.vehicle_type) {
      qs.vehicle_type = params.vehicle_type;
    }

    if (params.operator_id) {
      qs.operator_id = params.operator_id;
    }

    if (params.with_ratings_breakdown) {
      qs.with_ratings_breakdown = "true";
    }

    return this.request({
      url: url,
      qs,
      default_params: false,
      method: "GET",
      // There is a lot of potential variability in qs, and we don't want to overload the memory with cache
      // So caching only the calls that are the most used, the ones with profile_id
      http_caching_enabled: Object.keys(qs).length === 1 && "profile_id" in qs
    })
      .then(response => {
        if (response.body) {
          const {
            count_total,
            count_for_lang,
            ratings_breakdown,
            global_rating,
            staff_rating,
            timeliness_rating,
            seats_rating,
            bus_temperature_rating,
            departure_location_rating,
            cleanliness_rating,
            wifi_rating,
            power_outlets_rating,
            value_for_money_rating,
            ticket_access_rating
          } = Object.assign(EMPTY_STATS, response.body);

          return {
            count_total,
            count_for_lang,
            ratings_breakdown,
            global_rating,
            staff_rating,
            timeliness_rating,
            seats_rating,
            bus_temperature_rating,
            departure_location_rating,
            cleanliness_rating,
            wifi_rating,
            power_outlets_rating,
            value_for_money_rating,
            ticket_access_rating
          };
        }

        return EMPTY_STATS;
      })
      .catch(() => {
        return EMPTY_STATS;
      });
  }

  public updateUserPreferences(
    user_preferences: UserPreferences
  ): Promise<UserPreferences> {
    const url = `/settings/user-preferences`;

    return this.request({
      url,
      method: "PUT",
      data: user_preferences
    }).then(response => response.body);
  }

  public createInstallmentsPlans(
    params: InstallmentsPlansParams
  ): Promise<InstallmentsPlan[]> {
    const url = `${this.options.domain}/installments-plans`;

    return this.request({
      url,
      method: "POST",
      data: params
    }).then(response => response.body);
  }

  // list candidate and selected cart items
  public fetchCartItems(
    cart_id: string,
    currency: CurrencyCode,
    country_code3: string | null
  ): Promise<Ancillary[]> {
    const url = `${this.options.domain}/cart/${cart_id}/items`;

    return this.request({
      url,
      qs: {
        currency,
        country_code3
      },
      method: "GET"
    }).then(response => response.body);
  }

  // This adds a cart item candidate as a cart item in the cart
  public addCartItemCandidate(
    cart_id: string,
    cart_item_id: Ancillary["id"]
  ): Promise<Ancillary> {
    const url = `${this.options.domain}/cart/${cart_id}/items`;

    return this.request({
      url,
      method: "POST",
      data: { cart_item_id }
    }).then(response => response.body);
  }

  // This removes the cart item from the cart
  public removeCartItem(
    cart_id: CartAdapter["id"],
    cart_item_id: Ancillary["id"]
  ): Promise<Ancillary> {
    const url = `${this.options.domain}/cart/${cart_id}/items/${cart_item_id}`;

    return this.request({
      url,
      method: "DELETE"
    }).then(response => response.body);
  }

  public postActionCompleted(
    purchase_id: number,
    purchase_uuid: string,
    action_payload: unknown
  ): Promise<void> {
    const url = `${this.options.domain}/purchases/${purchase_id}/additional-action-results`;

    return this.request({
      url: url,
      method: "post",
      "axios-retry": CART_RETRY_CONFIGURATION,
      qs: {
        purchase_uuid
      },
      headers: { Accept: ACCEPT_HEADER_V3 },
      data: action_payload
    }).then(() => {});
  }

  public validateMerchantForApplePay(validation_url: string): Promise<unknown> {
    return this.request({
      url: APPLE_PAY_VALIDATE_MERCHANT_URL,
      method: "post",
      data: {
        validation_url
      }
    }).then(response => response.body);
  }

  public getCartReward(cart_id: string): Promise<NapiAmount> {
    const url = `${this.options.domain}/carts/${cart_id}/reward`;
    return this.request({
      url,
      method: "GET"
    }).then(response => response.body);
  }

  public postRedeemCartReward(cart_id: string): Promise<NapiRedeemableAmounts> {
    const url = `${this.options.domain}/carts/${cart_id}/redeem`;
    return this.request({
      url,
      method: "POST",
      headers: { Accept: ACCEPT_HEADER_V3 }
    }).then(response => response.body);
  }

  public postUnredeemCartReward(
    cart_id: string
  ): Promise<{ success: boolean; reason?: string }> {
    const url = `${this.options.domain}/carts/${cart_id}/unredeem`;
    return this.request({
      url,
      method: "POST",
      headers: { Accept: ACCEPT_HEADER_V3 }
    }).then(response => response.body);
  }

  public getRedeemableAmounts(
    user_id?: number,
    country_code?: string
  ): Promise<NapiRedeemableAmounts | undefined> {
    if (!user_id || !country_code) {
      return Promise.resolve(undefined);
    }

    const url = `${this.options.domain}/users/${user_id}/redeemable_amounts`;
    return this.request({
      url,
      method: "GET",
      qs: {
        lang: this.options.lang,
        locale: this.options.locale,
        country_code
      },
      headers: { Accept: ACCEPT_HEADER_V3 }
    })
      .then(response => response.body)
      .catch(() => {});
  }

  public addDiscountCodeV2(cart_id: string, discount_code: string) {
    const url = `${this.options.domain}/carts/${cart_id}/discount`;

    return this.request({
      url,
      method: "POST",
      headers: { Accept: ACCEPT_HEADER_V3 },
      data: {
        discount_code
      }
    }).then(response => response.body.data);
  }

  public removeDiscountCodeV2(cart_id: string, discount_code: string) {
    const url = `${this.options.domain}/carts/${cart_id}/discount/${discount_code}`;

    return this.request({
      url,
      method: "DELETE",
      headers: { Accept: ACCEPT_HEADER_V3 }
    }).then(response => response.body.data);
  }

  public getAvailableAncillariesForCart(
    cart_id: string,
    filter?: Ancillary["category"][]
  ): Promise<Ancillary[]> {
    const url = `${this.options.domain}/carts/${cart_id}/ancillaries`;

    return this.request({
      url,
      qs: {
        ...(filter && { filter: filter.join(",") })
      },
      method: "GET",
      "axios-retry": CART_RETRY_CONFIGURATION,
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      }
    })
      .then(response => {
        return response.body;
      })
      .catch(() => {
        return [];
      });
  }

  public async selectAncillaryForCart(
    cart_id: string,
    ancillary_id: Ancillary["id"]
  ): Promise<NapiV2Cart> {
    const url = `${this.options.domain}/carts/${cart_id}/ancillaries/${ancillary_id}`;

    const response = await this.request({
      url,
      method: "POST",
      "axios-retry": CART_RETRY_CONFIGURATION,
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      },
      data: { id: ancillary_id }
    });
    return response.body;
  }

  public async unselectAncillaryForCart(
    cart_id: string,
    ancillary_id: Ancillary["id"]
  ): Promise<NapiV2Cart> {
    const url = `${this.options.domain}/carts/${cart_id}/ancillaries/${ancillary_id}`;

    const response = await this.request({
      url,
      method: "DELETE",
      "axios-retry": CART_RETRY_CONFIGURATION,
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      }
    });
    return response.body;
  }

  public purchaseCartV2(data: NapiV2PurchaseCartParams): Promise<CartPurchase> {
    const url = `${this.options.domain}/purchases`;
    return this.request({
      url,
      method: "POST",
      "axios-retry": CART_RETRY_CONFIGURATION,
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      },
      data,
      timeout: CART_PURCHASE_TIMEOUT
    }).then(response => {
      return response.body;
    });
  }

  public async acceptNewCharges(cart_id: string): Promise<void> {
    const url = `${this.options.domain}/carts/${cart_id}/charges`;
    await this.request({
      url,
      method: "PUT",
      "axios-retry": CART_RETRY_CONFIGURATION,
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      }
    });
  }

  public getLatestCharges(cart_id: string): Promise<NapiV2CartCharges> {
    const url = `${this.options.domain}/carts/${cart_id}/charges`;
    return this.request({
      url,
      method: "GET",
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      }
    }).then(response => {
      return response.body;
    });
  }

  public async getPointOfInterest(
    poi_id: string,
    lang: SupportedLanguage
  ): Promise<PointOfInterest | null> {
    const url = `${this.options.domain}/points-of-interest/${poi_id}/localized`;
    return this.request({
      url,
      method: "GET",
      qs: {
        lang
      }
    })
      .then(response => {
        return response.body;
      })
      .catch(err => {
        if (err?.response?.status === 404) {
          return null;
        }
        throw err;
      });
  }

  public async getPopularPlaces(
    id_or_geohash: string,
    lang: SupportedLanguage,
    limit?: number,
    vehicle_type?: VehicleType
  ): Promise<NapiPopularPlace[]> {
    const url = `${this.options.domain}/places/${id_or_geohash}/popular-places`;

    const qs = {
      lang,
      limit,
      vehicle_type
    };

    return this.request({
      url,
      method: "GET",
      qs,
      default_params: false
    }).then(response => {
      return response.body;
    });
  }

  public async getProfileReviewsSummaries({
    profile_id,
    lang
  }: {
    profile_id: number;
    lang: SupportedLanguage;
  }): Promise<GrandCentral.API.TravelReviewSummary[]> {
    const url = `${this.options.domain}/profile-reviews-summaries`;

    return this.request({
      url,
      method: "GET",
      qs: {
        lang,
        profile_id
      },
      default_params: false
    }).then(response => {
      return response.body;
    });
  }

  public async getReviewsSummaries(
    params:
      | {
          origin_identifier: string;
          destination_identifier: string;
          profile_id: number;
        }
      | {
          origin_identifier: string;
          destination_identifier: string;
          profile_ids: number[];
        }
  ): Promise<GrandCentral.API.TravelReviewSummary[]> {
    const url = `${this.options.domain}/travel-reviews-summaries`;
    const qs: Record<string, string | undefined | number> = {
      origin_identifier: params.origin_identifier,
      destination_identifier: params.destination_identifier,
      lang: this.options.lang
    };
    if ("profile_id" in params) {
      qs["profile_id"] = params.profile_id;
    } else {
      qs["profile_ids"] = params.profile_ids.join(",");
    }

    return this.request({
      url,
      method: "GET",
      qs,
      default_params: false
    })
      .then(response => {
        return response.body;
      })
      .catch(err => {
        sentry.captureException(err, {
          tags: {
            request: "get_reviews_summaries"
          }
        });

        return [];
      });
  }

  public getAvailableInstallmentsForCartV2(
    cart_id: string,
    bin?: string
  ): Promise<NapiV2InstallmentsPlan[]> {
    const url = `${this.options.domain}/carts/${cart_id}/installments`;
    const qs = {
      bin: bin
    };

    return this.request({
      url,
      method: "GET",
      qs,
      "axios-retry": CART_RETRY_CONFIGURATION,
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      }
    })
      .then(response => {
        return response.body;
      })
      .catch(() => {
        return [];
      });
  }

  public selectInstallmentsForCart(
    cart_id: string,
    installments_plan_id: string
  ): Promise<BillingInfo | null> {
    const url = `${this.options.domain}/carts/${cart_id}/installments`;

    return this.request({
      url,
      method: "POST",
      "axios-retry": CART_RETRY_CONFIGURATION,
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      },
      data: { installments_plan_id }
    }).then(response => {
      return response.body;
    });
  }

  public unselectInstallmentsForCart(
    cart_id: string,
    installments_plan_id: string
  ): Promise<{}> {
    const url = `${this.options.domain}/carts/${cart_id}/installments/${installments_plan_id}`;

    return this.request({
      url,
      method: "DELETE",
      "axios-retry": CART_RETRY_CONFIGURATION,
      headers: {
        "Cache-Control": "no-cache",
        Accept: ACCEPT_HEADER_V3
      }
    }).then(response => {
      return response.body;
    });
  }

  public async getOperatorDetails(
    operator_id: string
  ): Promise<OperatorDetails> {
    const url = `${this.options.domain}/operators/${operator_id}/details?extra_fields=profile,source`;

    return this.request({
      url,
      method: "GET"
    }).then(response => {
      return response.body;
    });
  }

  public async getSourcesByPartner(
    partner_id: string
  ): Promise<SourceAccessListResponse> {
    const url = `${this.options.domain}/partners/${partner_id}/sources-access-list`;

    const qs = {
      sources_restriction: "allowlist"
    };

    return this.request({
      url,
      method: "GET",
      qs
    }).then(response => response.body);
  }

  public async getProfileByPartner(
    partner_id: string | number
  ): Promise<NapiPartnerProfile[]> {
    const url = `${this.options.domain}/partners/${partner_id}/profiles`;

    return this.request({
      url,
      method: "GET"
    }).then(response => response.body);
  }

  public async getFlexGeoEntitiesSuggestions({
    whitelabel,
    ids,
    lang
  }: {
    whitelabel?: DynamicWhitelabel;
    ids: string[];
    lang: SupportedLanguage;
  }): Promise<{ entities: { id: string }[] }> {
    const url = `${this.options.domain}/flex/suggestions/geo-entities`;

    const response = await this.request({
      url,
      method: "GET",
      qs: {
        ids: ids.join(","),
        lang
      },
      headers: whitelabel ? { "x-whitelabel": whitelabel.name } : undefined
    });

    return response.body;
  }
}

export default NapiClient;
